import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { webhookEventOptions, webhookResourceTypeOptions } from 'src/constants/webhook/constants'
import type { SendTestWebhookFormValues } from './SendTestWebhookModal.interface'
import { useSendTestWebhookGate } from './useSendTestWebhookGate'
import { GSIcon } from 'src/components/shared/GSIcon'

/**
 * The form body: live-request callout, the three fields, and the reason a send would be
 * rejected (stated inline rather than as a bare greyed button). Lives inside the Formik tree so
 * the reason recomputes as the event select changes; the submit sits in the modal footer.
 */
const SendTestWebhookFieldsComponent = () => {
  const { blockedReason } = useSendTestWebhookGate()

  return (
    <Fragment>
      <div css={tw`flex flex-row items-start gap-3 rounded-lg bg-warning-100 p-4`}>
        <GSIcon iconName="gs_exclamation_triangle" size={20} css={tw`shrink-0 text-warning-500`} />
        <Typography variant="body2">
          This sends a <b>real, live HTTP request</b> to the customer&apos;s callback URL using the
          values below. The data is test data; the request is not.
        </Typography>
      </div>
      <div css={tw`mt-4 flex flex-col gap-4`}>
        <FormikSelect<SendTestWebhookFormValues>
          name="eventType"
          label="Event to simulate"
          options={webhookEventOptions}
          placeholder="Choose an event"
          data-testid="send-test-webhook-event-select"
        />
        <FormikSelect<SendTestWebhookFormValues>
          name="resourceType"
          label="Resource type"
          options={webhookResourceTypeOptions}
          data-testid="send-test-webhook-resource-type-select"
        />
        <FormikTextField<SendTestWebhookFormValues>
          name="resourceId"
          label="Resource ID"
          placeholder="The driver, payment, or onboarding-data ID to reference"
          data-testid="send-test-webhook-resource-id-input"
        />
      </div>
      {blockedReason ? (
        <Typography
          variant="body2"
          css={tw`mt-3 text-shades-600`}
          data-testid="send-test-webhook-blocked-reason"
        >
          {blockedReason}
        </Typography>
      ) : null}
    </Fragment>
  )
}

export const SendTestWebhookFields = memo(SendTestWebhookFieldsComponent)
